"use client";

import type React from "react";

import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Lightning } from "geist-icons";
import { useTheme } from "next-themes";

export const PulseBeams = () => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="relative flex flex-col items-center justify-center">
      <div className="relative h-[600px] mx-auto">
        {/* Everything scales together in this container */}
        <div
          className="
    absolute inset-0
    scale-40
    [@media(min-width:451px)_and_(max-width:639px)]:scale-53
    sm:scale-75
    md:scale-85
    lg:scale-100
  "
        >
          {/* SVG Container */}
          <BeamsSVG isHovered={isHovered} />

          {/* Center Chip */}
          <div
            className="absolute left-1/2 top-[3%] sm:top-[28%] md:top-[53.3%] -translate-x-[48%] bg-zinc-200 dark:bg-zinc-800/90 backdrop-blur-sm w-[220px] h-[80px] z-40 rounded-lg shadow-xl border border-[#EAEAEA] dark:border-[#1E1E1E]"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Pins on top */}
            <div className="absolute -top-1 left-0 right-0 flex justify-center space-x-3">
              {[...Array(6)].map((_, i) => (
                <div key={`top-pin-${i}`} className="w-1 h-3 bg-zinc-400 dark:bg-zinc-600" />
              ))}
            </div>

            {/* Pins on bottom */}
            <div className="absolute -bottom-1 left-0 right-0 flex justify-center space-x-3">
              {[...Array(6)].map((_, i) => (
                <div key={`bottom-pin-${i}`} className="w-1 h-3 bg-zinc-400 dark:bg-zinc-600" />
              ))}
            </div>

            {/* Pins on left */}
            <div className="absolute -left-1 top-0 bottom-0 flex flex-col justify-center space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={`left-pin-${i}`} className="h-1 w-3 bg-zinc-400 dark:bg-zinc-600" />
              ))}
            </div>

            {/* Pins on right */}
            <div className="absolute -right-1 top-0 bottom-0 flex flex-col justify-center space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={`right-pin-${i}`} className="h-1 w-3 bg-zinc-400 dark:bg-zinc-600" />
              ))}
            </div>

            <div className="flex items-center justify-center h-full">
              <span className="text-2xl font-medium">AI Power</span>
            </div>

            {/* Hover glow effect */}
            <div
              className={`absolute inset-0 rounded-lg transition-opacity duration-300 ${
                isHovered ? "opacity-100" : "opacity-0"
              }`}
            >
              <div className="absolute inset-0 rounded-lg bg-linear-to-r from-white via-gray-500/50 to-black blur-md cursor-pointer" />
            </div>
          </div>

          {/* Technology Cards */}
          <div className="absolute -bottom-[20%] sm:-bottom-[20%] md:-bottom-[20%] left-1/2 -translate-x-1/2 flex justify-center gap-8 lg:flex-row md:flex-row flex-col md:w-auto sm:gap-4">
            <TechCard
              title="v0"
              className="bg-[radial-gradient(circle_280px_at_50%_0%,#BFBFBF,#F2F3F3)] md:bg-[radial-gradient(circle_280px_at_100%_20%,#BFBFBF,#F2F3F3)] dark:bg-[radial-gradient(circle_280px_at_50%_0%,#444444,#0c0d0d)] dark:md:bg-[radial-gradient(circle_280px_at_100%_20%,#444444,#0c0d0d)]"
              logo={
                <svg
                  viewBox="0 0 18 18"
                  className="w-10 h-10 text-black dark:text-white"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.50321 5.5H13.2532C13.3123 5.5 13.3704 5.5041 13.4273 5.51203L9.51242 9.42692C9.50424 9.36912 9.5 9.31006 9.5 9.25L9.5 5.5L8 5.5L8 9.25C8 10.7688 9.23122 12 10.75 12H14.5V10.5L10.75 10.5C10.6899 10.5 10.6309 10.4958 10.5731 10.4876L14.4904 6.57028C14.4988 6.62897 14.5032 6.68897 14.5032 6.75V10.5H16.0032V6.75C16.0032 5.23122 14.772 4 13.2532 4H9.50321V5.5ZM0 5V5.00405L5.12525 11.5307C5.74119 12.3151 7.00106 11.8795 7.00106 10.8822V5H5.50106V9.58056L1.90404 5H0Z"
                    fill="currentColor"
                  ></path>{" "}
                </svg>
              }
              description="AI-Powered tool that generates UI from natural language description."
              position="left"
            />
            <TechCard
              title="AI SDK"
              className="bg-[radial-gradient(circle_280px_at_50%_0%,#BFBFBF,#F2F3F3)] dark:bg-[radial-gradient(circle_280px_at_50%_0%,#444444,#0c0d0d)]"
              logo={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="311"
                  height="90"
                  fill="none"
                  viewBox="0 0 310 90"
                  className="w-18 h-10 text-black dark:text-white"
                >
                  <path
                    fill="currentColor"
                    d="M2.973 86 32.669 3.64h18.444L80.809 86H65.033l-6.844-19.488H25.593L18.749 86zM30 53.636h23.78l-11.832-34.8zM130.894 3.64v13.108H117.67v56.144h13.92V86H88.554V72.892h14.036V16.748H89.25V3.64z"
                  ></path>
                  <rect
                    width="154"
                    height="75"
                    x="153"
                    y="7.5"
                    stroke="currentColor"
                    strokeWidth="8"
                    rx="37.5"
                  ></rect>
                  <path
                    fill="currentColor"
                    d="M183.603 51.847c.79 4.621 4.074 7.661 9.485 7.661 4.134 0 6.931-1.642 6.87-4.803s-2.736-4.986-9.485-6.627c-9.363-2.25-15.382-6.02-15.382-12.95 0-7.844 6.566-12.769 16.416-12.769 9.606 0 16.173 5.898 17.206 14.592l-9.241.487c-.487-4.56-3.648-7.418-8.208-7.418-4.013 0-6.871 2.067-6.688 5.229.121 3.648 4.377 4.864 8.998 6.019 9.667 2.128 15.808 6.262 15.808 13.133 0 8.208-7.235 12.829-16.537 12.829-10.519 0-17.936-5.837-18.544-14.957zm43.17-28.515c13.984 0 21.828 7.782 21.828 21.645 0 13.801-7.722 21.523-21.463 21.523h-15.261V23.332zm-5.654 35.386h5.654c8.391 0 12.404-4.439 12.404-13.802s-4.013-13.802-12.404-13.802h-5.654zm29.312-35.386h9.241V42.18l15.687-18.848h10.761l-15.868 19.03L287.093 66.5h-10.518l-12.403-17.936-4.5 5.229V66.5h-9.241z"
                  ></path>
                </svg>
              }
              description="TypeScript Toolkit for building AI full stack apps of generative content."
              position="center"
            />
            <TechCard
              title="Speed"
              className="bg-[radial-gradient(circle_280px_at_50%_0%,#BFBFBF,#F2F3F3)] md:bg-[radial-gradient(circle_280px_at_0%_50%,#BFBFBF,#F2F3F3)] dark:bg-[radial-gradient(circle_280px_at_50%_0%,#444444,#0c0d0d)] dark:md:bg-[radial-gradient(circle_280px_at_0%_50%,#444444,#0c0d0d)]"
              logo={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  strokeLinejoin="round"
                  color="currentcolor"
                  data-testid="geist-icon"
                  viewBox="0 0 24 16"
                  className="w-10 h-10 text-black dark:text-white"
                >
                  <path
                    fill="currentColor"
                    fillRule="evenodd"
                    d="M7.157 0 2.333 9.408l-.56 1.092H7a.25.25 0 0 1 .25.25V16h1.593l4.824-9.408.56-1.092H9a.25.25 0 0 1-.25-.25V0zM7 9H4.227L7.25 3.106V5.25C7.25 6.216 8.034 7 9 7h2.773L8.75 12.894V10.75A1.75 1.75 0 0 0 7 9"
                    clipRule="evenodd"
                  ></path>
                </svg>
              }
              description="Low-latency AI responses for fast, globally distributed responses."
              position="right"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const TechCard = ({
  title,
  logo,
  description,
  position,
  className,
}: {
  title: string;
  logo: React.ReactNode;
  description: string;
  position: "left" | "center" | "right";
  className: string;
}) => {
  const baseCardClass = `
    relative border border-[#EAEAEA] dark:border-[#1E1E1E] rounded-xl p-6 w-[750px] sm:w-[760px] h-[140px] sm:h-[100px] md:h-[190px] lg:w-[300px] md:w-[250px] flex flex-col z-[40] overflow-hidden
    before:content-[''] before:absolute before:top-0 before:left-1/4 before:w-[50%] 
    before:h-[2px] before:bg-gradient-to-r before:from-transparent 
    dark:before:via-white before:via-black before:to-transparent
  `;

  return (
    <div className={`${baseCardClass} ${className}`} id={`card-${position}`}>
      <div className="flex flex-row md:flex-col">
        <div className="mb-2 md:mb-4">{logo}</div>
        <div className="flex flex-col pl-4 md:p-0 gap-2 md:gap-0">
          <h3 className="text-2xl md:text-xl font-semibold text-black dark:text-white md:mb-2 flex items-center">
            {title} <ArrowUpRight className="ml-2 text-[#666666] dark:text-[#A0A0A0]" />
          </h3>
          <p className="text-2xl sm:text-lg md:text-sm text-[#666666] dark:text-[#A0A0A0] line-clamp-3">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

const BeamsSVG = ({ isHovered }: { isHovered: boolean }) => {
  const [pathLengths, setPathLengths] = useState<number[]>([]);
  const svgRef = useRef<SVGSVGElement>(null);
  const [hasMounted, setHasMounted] = useState(false);
  const { theme, resolvedTheme } = useTheme();

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const currentTheme = resolvedTheme || theme;
  const isDark = currentTheme === "dark";

  useEffect(() => {
    if (svgRef.current) {
      const lengths = cardConnectedPaths.map((path) => {
        const dummyPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
        dummyPath.setAttribute("d", path);
        return dummyPath.getTotalLength();
      });
      setPathLengths(lengths);
    }
  }, []);

  // Center point coordinates path
  const centerX = 550;
  const centerY = 360;

  // Card positions path
  const leftCardX = 100;
  const centerCardX = 600;
  const rightCardX = 1000;
  const cardsY = 560;

  // Center chip dimensions path
  const chipWidth = 220;
  const chipHeight = 80;
  const chipLeft = centerX - chipWidth / 2;
  const chipRight = centerX + chipWidth / 2;
  const chipTop = centerY - chipHeight / 2;
  const chipBottom = centerY + chipHeight / 2;

  // Pin positions (exact coordinates)
  const leftPins = [
    { x: chipLeft - 3, y: centerY - 20 },
    { x: chipLeft - 3, y: centerY },
    { x: chipLeft - 3, y: centerY + 20 },
  ];

  const rightPins = [
    { x: chipRight + 3, y: centerY - 20 },
    { x: chipRight + 3, y: centerY },
    { x: chipRight + 3, y: centerY + 20 },
  ];

  const bottomPins = [
    { x: centerX - 50, y: chipBottom + 3 },
    { x: centerX - 30, y: chipBottom + 3 },
    { x: centerX - 10, y: chipBottom + 3 },
    { x: centerX + 10, y: chipBottom + 3 },
    { x: centerX + 30, y: chipBottom + 3 },
    { x: centerX + 50, y: chipBottom + 3 },
  ];

  const topPins = [
    { x: centerX - 50, y: chipTop - 3 },
    { x: centerX - 30, y: chipTop - 3 },
    { x: centerX - 10, y: chipTop - 3 },
    { x: centerX + 10, y: chipTop - 3 },
    { x: centerX + 30, y: chipTop - 3 },
    { x: centerX + 50, y: chipTop - 3 },
  ];

  // All paths that connect to cards (with dash animation)
  const cardConnectedPaths = [
    // Left card to left pins (2 paths)
    `M${leftCardX + 150},${cardsY} L${leftCardX + 150},${cardsY - 50} L${
      leftPins[0].x - 50
    },${cardsY - 50} L${leftPins[0].x - 50},${leftPins[0].y} L${leftPins[0].x},${leftPins[0].y}`,
    `M${leftCardX + 100},${cardsY} L${leftCardX + 100},${cardsY - 100} L${
      leftPins[2].x - 20
    },${cardsY - 100} L${leftPins[2].x - 20},${leftPins[2].y} L${leftPins[2].x},${leftPins[2].y}`,

    // Center card to bottom pins (2 paths)
    `M${centerCardX},${cardsY} L${centerCardX},${cardsY} L${bottomPins[1].x},${cardsY} L${bottomPins[1].x},${bottomPins[1].y}`,
    `M${centerCardX},${cardsY} L${centerCardX},${cardsY} L${bottomPins[4].x},${cardsY} L${bottomPins[4].x},${bottomPins[4].y}`,

    // Right card to right pins (2 paths)
    `M${rightCardX - 150},${cardsY} L${rightCardX - 150},${cardsY - 50} L${
      rightPins[0].x + 50
    },${cardsY - 50} L${rightPins[0].x + 50},${rightPins[0].y} L${
      rightPins[0].x
    },${rightPins[0].y}`,
    `M${rightCardX - 100},${cardsY} L${rightCardX - 100},${cardsY - 100} L${
      rightPins[2].x + 20
    },${cardsY - 100} L${rightPins[2].x + 20},${rightPins[2].y} L${
      rightPins[2].x
    },${rightPins[2].y}`,

    // Top pins to left card (shorter path)
    `M${topPins[1].x},${topPins[1].y} L${topPins[1].x},${topPins[1].y - 20} L${
      leftCardX + 120
    },${topPins[1].y - 20} L${leftCardX + 120},${cardsY}`,

    // Top pins to right card (shorter path)
    `M${topPins[4].x},${topPins[4].y} L${topPins[4].x},${topPins[4].y - 20} L${
      rightCardX - 120
    },${topPins[4].y - 20} L${rightCardX - 120},${cardsY}`,

    // Bottom pins to left card
    `M${bottomPins[0].x},${bottomPins[0].y} L${bottomPins[0].x},${
      bottomPins[0].y + 30
    } L${leftCardX + 80},${bottomPins[0].y + 30} L${leftCardX + 80},${cardsY}`,

    // Bottom pins to right card
    `M${bottomPins[5].x},${bottomPins[5].y} L${bottomPins[5].x},${
      bottomPins[5].y + 30
    } L${rightCardX - 80},${bottomPins[5].y + 30} L${rightCardX - 80},${cardsY}`,
  ];

  // Short non-animated paths (with dots at the end)
  const shortNonAnimatedPaths = [
    // From top pins - very short
    `M${topPins[0].x},${topPins[0].y} L${topPins[0].x},${topPins[0].y - 30}`,
    `M${topPins[5].x},${topPins[5].y} L${topPins[5].x},${topPins[5].y - 30}`,

    // From right pins - medium short with one turn
    `M${rightPins[1].x},${rightPins[1].y} L${rightPins[1].x + 40},${
      rightPins[1].y
    } L${rightPins[1].x + 40},${rightPins[1].y + 20}`,

    // From left pins - medium short with one turn
    `M${leftPins[1].x},${leftPins[1].y} L${leftPins[1].x - 40},${
      leftPins[1].y
    } L${leftPins[1].x - 40},${leftPins[1].y + 20}`,

    // From top pins - medium with one turn
    `M${topPins[2].x},${topPins[2].y} L${topPins[2].x},${topPins[2].y - 40} L${
      topPins[2].x - 30
    },${topPins[2].y - 40}`,
    `M${topPins[3].x},${topPins[3].y} L${topPins[3].x},${topPins[3].y - 40} L${
      topPins[3].x + 30
    },${topPins[3].y - 40}`,

    // From bottom pins - medium with one turn
    `M${bottomPins[2].x},${bottomPins[2].y} L${bottomPins[2].x},${
      bottomPins[2].y + 40
    } L${bottomPins[2].x - 30},${bottomPins[2].y + 40}`,
    `M${bottomPins[3].x},${bottomPins[3].y} L${bottomPins[3].x},${
      bottomPins[3].y + 40
    } L${bottomPins[3].x + 30},${bottomPins[3].y + 40}`,
  ];

  // Dot endpoints for non-animated paths only
  const dotEndpoints = [
    { x: topPins[0].x, y: topPins[0].y - 30 },
    { x: topPins[5].x, y: topPins[5].y - 30 },
    { x: rightPins[1].x + 40, y: rightPins[1].y + 20 },
    { x: leftPins[1].x - 40, y: leftPins[1].y + 20 },
    { x: topPins[2].x - 30, y: topPins[2].y - 40 },
    { x: topPins[3].x + 30, y: topPins[3].y - 40 },
    { x: bottomPins[2].x - 30, y: bottomPins[2].y + 40 },
    { x: bottomPins[3].x + 30, y: bottomPins[3].y + 40 },
  ];

  if (!hasMounted && !resolvedTheme) {
    return (
      <svg
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute inset-0 -translate-x-5/11 -translate-y-1/2 sm:-translate-y-1/4 md:-translate-y-0 w-[1200px] h-[600px]"
      >
        {/* Base paths for animated paths (always visible in grey) */}
        {cardConnectedPaths.map((path, index) => (
          <path
            key={`base-${index}`}
            d={path}
            strokeWidth="2"
            fill="none"
            className="stroke-[#EAEAEA] dark:stroke-[#1E1E1E]"
          />
        ))}

        {/* Non-animated short paths */}
        {shortNonAnimatedPaths.map((path, index) => (
          <path
            key={`non-animated-${index}`}
            d={path}
            className="stroke-[#EAEAEA] dark:stroke-[#1E1E1E]"
            strokeWidth="2"
            fill="none"
          />
        ))}

        {/* Dots ONLY at the end of non-animated paths */}
        {dotEndpoints.map((point, index) => (
          <circle
            key={`dot-endpoint-${index}`}
            cx={point.x}
            cy={point.y}
            r={4}
            fill="bg-[#EAEAEA] dark:bg-[#1E1E1E]"
            className="stroke-[#EAEAEA] dark:stroke-[#1E1E1E]"
            strokeWidth="3"
          />
        ))}
      </svg>
    );
  }
  return (
    <svg
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="absolute inset-0 -translate-x-5/11 -translate-y-1/2 sm:-translate-y-1/4 md:-translate-y-0 w-[1200px] h-[600px]"
    >
      {/* Base paths for animated paths (always visible in grey) */}
      {cardConnectedPaths.map((path, index) => (
        <path
          key={`base-${index}`}
          d={path}
          stroke={isDark ? "#1E1E1E" : "#EAEAEA"}
          strokeWidth="2"
          fill="none"
        />
      ))}

      {/* Non-animated short paths */}
      {shortNonAnimatedPaths.map((path, index) => (
        <path
          key={`non-animated-${index}`}
          d={path}
          stroke={isDark ? "#1E1E1E" : "#EAEAEA"}
          strokeWidth="2"
          fill="none"
        />
      ))}

      {/* Animated paths */}
      {cardConnectedPaths.map((path, index) => {
        // Get the path length or use a default if not calculated yet
        const pathLength = pathLengths[index] || 1000;

        return (
          <motion.path
            key={`animated-${index}`}
            d={path}
            stroke={`url(#grad${(index % 5) + 1})`}
            strokeWidth="1.5"
            strokeDasharray={`40 ${pathLength - 40}`}
            fill="none"
            initial={{ strokeDashoffset: pathLength }}
            animate={{ strokeDashoffset: 0 }}
            transition={{
              duration: 3 + (index % 3) * 0.5,
              ease: "easeInOut",
              repeat: Number.POSITIVE_INFINITY,
              repeatType: "loop",
            }}
          />
        );
      })}

      {/* Dots ONLY at the end of non-animated paths */}
      {dotEndpoints.map((point, index) => (
        <circle
          key={`dot-endpoint-${index}`}
          cx={point.x}
          cy={point.y}
          r={4}
          fill={isDark ? "black" : "white"}
          stroke={isDark ? "#1E1E1E" : "#EAEAEA"}
          strokeWidth="3"
        />
      ))}

      {/* Gradient Definitions */}
      <defs>
        <linearGradient id="grad1" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={isDark ? "#929292" : "#737373"} stopOpacity="0" />
          <stop offset="20%" stopColor={isDark ? "#515151" : "#B3B3B3"} />
          <stop offset="50%" stopColor={isDark ? "#F8F8F8" : "#0D0D0D"} />
          <stop offset="100%" stopColor={isDark ? "#616161" : "#999999"} stopOpacity="0" />
        </linearGradient>

        <linearGradient id="grad2" x1="100%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor={isDark ? "#E0E0E0" : "#1A1A1A"} stopOpacity="0" />
          <stop offset="20%" stopColor={isDark ? "#B7B7B7" : "#4D4D4D"} />
          <stop offset="100%" stopColor={isDark ? "#595959" : "#A6A6A6"} />
          <stop offset="100%" stopColor={isDark ? "#A3A3A3" : "#595959"} stopOpacity="0" />
        </linearGradient>

        <linearGradient id="grad3" x1="100%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor={isDark ? "#BBBBBB" : "#4F4F4F"} stopOpacity="0" />
          <stop offset="20%" stopColor={isDark ? "#4D4D4D" : "#BFBFBF"} />
          <stop offset="100%" stopColor={isDark ? "#F0F0F0" : "#0E0E0E"} />
          <stop offset="100%" stopColor={isDark ? "#767676" : "#8C8C8C"} stopOpacity="0" />
        </linearGradient>

        <linearGradient id="grad4" x1="100%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor={isDark ? "#ECECEC" : "#0F0F0F"} stopOpacity="0" />
          <stop offset="20%" stopColor={isDark ? "#454545" : "#B5B5B5"} />
          <stop offset="50%" stopColor={isDark ? "#DCDCDC" : "#262626"} />
          <stop offset="100%" stopColor={isDark ? "#C7C7C7" : "#333333"} stopOpacity="0" />
        </linearGradient>

        <linearGradient id="grad5" x1="100%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor={isDark ? "#D0D0D0" : "#404040"} stopOpacity="0" />
          <stop offset="20%" stopColor={isDark ? "#8A8A8A" : "#757575"} />
          <stop offset="50%" stopColor={isDark ? "#727272" : "#8D8D8D"} />
          <stop offset="100%" stopColor={isDark ? "#D8D8D8" : "#272727"} stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
};
